import { useReactiveVar } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import useMediaQuery from '@/hooks/useMediaQuery';
import { currentAgencyVar } from '@/util/apollo/cache';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import Chip from '@/components/Chip';
import TableHeader from '@/components/TableHeader';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import Link from '@/elements/Link';
import IconicButton from '@/components/IconicButton';
import {
  faEye,
  faExclamationTriangle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import TimesheetDetailsModal from './TimesheetDetailsModal';

type HealthCheckSummary = {
  accounts_analyzed: number;
  timesheets_analyzed: number;
  total_issues_found: number;
  critical_issues: number;
  high_issues: number;
  warning_issues: number;
  accounts_with_issues: number;
  ready_for_invoicing: boolean;
};

type TimesheetProblem = {
  type: string;
  severity: 'critical' | 'high' | 'warning';
  message: string;
  details: any;
};

type TimesheetIssues = {
  has_problems: boolean;
  problems_count: number;
  critical_count: number;
  high_count: number;
  warning_count: number;
  problem_list: TimesheetProblem[];
};

type Timesheet = {
  timesheet_id: number;
  worker: {
    id: number;
    name: string;
    email: string;
  };
  job: {
    id: number;
    title: string;
    first_shift_start_at: string;
    skill_name: string;
  };
  account: {
    id: number;
    name: string;
    cut_off: string;
  };
  approved_details: {
    approved_checkin_at: string | null;
    approved_checkout_at: string | null;
    approved_break_minutes: number;
    approved_minutes: number;
    approved_at: string;
    approved_by: {
      id: number | null;
      name: string | null;
      type: string | null;
    };
  };
  original_times: {
    checkin_at: string | null;
    checkout_at: string | null;
    reported_checkin_at: string | null;
    reported_checkout_at: string | null;
    reported_break_minutes: number;
    modified_checkin_at: string | null;
    modified_checkout_at: string | null;
    modified_break_minutes: number | null;
  };
  rates: {
    cost_rate: number;
    pay_rate: number;
    pay_overtime: number | null;
  };
  status: {
    invoiced: boolean;
    invoiceable: boolean;
    flagged: boolean;
    flag_reason: number;
    flag_reason_text: string;
  };
  issues: TimesheetIssues;
};

type HealthCheckResponse = {
  status: string;
  timestamp: string;
  summary: HealthCheckSummary;
  recommendations: string[];
  timesheets: Timesheet[];
};

const HEADER_FIELDS = [
  { name: 'TIMESHEET ID', style: { width: '10%' } },
  { name: 'WORKER', style: { width: '20%', color: 'black' } },
  { name: 'JOB', style: { width: '15%', color: 'black' } },
  { name: 'ACCOUNT', style: { width: '15%', color: 'black' } },
  { name: 'STATUS', style: { width: '10%', color: 'black' } },
  { name: 'ISSUES', style: { width: '15%', color: 'black' } },
  { name: 'DATE', style: { width: '10%', color: 'black' } },
  { name: 'ACTIONS', style: { width: '5%', color: 'black' } },
];

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getIssuesSeverityChip = (issues: TimesheetIssues) => {
  if (!issues.has_problems) {
    return <Chip color="success" label="No Issues" size="small" />;
  }

  if (issues.critical_count > 0) {
    return (
      <Chip
        color="error"
        label={`${issues.critical_count} Critical`}
        size="small"
      />
    );
  }

  if (issues.high_count > 0) {
    return (
      <Chip color="warning" label={`${issues.high_count} High`} size="small" />
    );
  }

  return (
    <Chip
      color="default"
      label={`${issues.warning_count} Warning`}
      size="small"
    />
  );
};

const getStatusChip = (timesheet: Timesheet) => {
  if (
    timesheet.approved_details.approved_by &&
    timesheet.approved_details.approved_by.id !== null
  ) {
    return <Chip color="success" label="Approved" size="small" />;
  }

  return <Chip color="default" label="Unapproved" size="small" />;
};

const TimesheetHealthChecks: React.FC = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [healthCheckData, setHealthCheckData] =
    useState<HealthCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    fetchHealthChecks();
  }, []);

  const fetchHealthChecks = async () => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get(
        '/api/timesheets/health_checks',
      );

      setHealthCheckData(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching timesheet health checks:', error);
      setError('Error loading timesheet health checks');
    } finally {
      setLoading(false);
    }
  };

  const handleShowTimesheet = (timesheet: Timesheet) => {
    setSelectedTimesheetId(timesheet.timesheet_id);
    setShowTimesheetModal(true);
  };

  const handleHideModal = () => {
    setShowTimesheetModal(false);
    setSelectedTimesheetId(null);
  };

  const getSummaryIcon = () => {
    if (!healthCheckData?.summary) return null;

    if (healthCheckData.summary.critical_issues > 0) {
      return faExclamationTriangle;
    }

    if (healthCheckData.summary.ready_for_invoicing) {
      return faCheckCircle;
    }

    return faExclamationTriangle;
  };

  const getSummaryColor = () => {
    if (!healthCheckData?.summary) return 'default';

    if (healthCheckData.summary.critical_issues > 0) {
      return 'error';
    }

    if (healthCheckData.summary.ready_for_invoicing) {
      return 'success';
    }

    return 'warning';
  };

  return (
    <Page
      size="full"
      title="Timesheet Health Checks"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchHealthChecks(),
      }}
    >
      {/* Summary Card */}
      {healthCheckData?.summary && (
        <Card>
          <Card.Section>
            <Stack gap={24} vertical>
              <Stack gap={16} align="center">
                <Body weight="semibold">Health Check Summary</Body>
                <Body>
                  Last updated:{' '}
                  {healthCheckData.timestamp
                    ? formatDate(healthCheckData.timestamp)
                    : 'N/A'}
                </Body>
              </Stack>

              <Stack gap={32} wrap>
                <Stack gap={8} vertical>
                  <Body weight="medium">Accounts Analyzed</Body>
                  <Body weight="bold">
                    {healthCheckData.summary.accounts_analyzed}
                  </Body>
                </Stack>

                <Stack gap={8} vertical>
                  <Body weight="medium">Timesheets Analyzed</Body>
                  <Body weight="bold">
                    {healthCheckData.summary.timesheets_analyzed}
                  </Body>
                </Stack>

                <Stack gap={8} vertical>
                  <Body weight="medium">Total Issues</Body>
                  <Body
                    weight="bold"
                    color={
                      healthCheckData.summary.total_issues_found > 0
                        ? 'danger'
                        : 'success'
                    }
                  >
                    {healthCheckData.summary.total_issues_found}
                  </Body>
                </Stack>

                <Stack gap={8} vertical>
                  <Body weight="medium">Ready for Invoicing</Body>
                  <Chip
                    color={
                      healthCheckData.summary.ready_for_invoicing
                        ? 'success'
                        : 'error'
                    }
                    label={
                      healthCheckData.summary.ready_for_invoicing ? 'Yes' : 'No'
                    }
                    size="medium"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Timesheets Table */}
      <Card>
        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading &&
            healthCheckData?.timesheets &&
            healthCheckData.timesheets.length > 0 ? (
              healthCheckData.timesheets
                .sort((a, b) => {
                  // Sort unapproved timesheets to the top
                  const aApproved =
                    a.approved_details.approved_by &&
                    a.approved_details.approved_by.id !== null;
                  const bApproved =
                    b.approved_details.approved_by &&
                    b.approved_details.approved_by.id !== null;

                  if (!aApproved && bApproved) return -1; // a (unapproved) comes first
                  if (aApproved && !bApproved) return 1; // b (unapproved) comes first
                  return 0; // maintain original order for same approval status
                })
                .map((timesheet) => (
                  <TableRow key={timesheet.timesheet_id}>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Body weight="medium">{timesheet.timesheet_id}</Body>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Link
                            id={`worker-link-${timesheet.worker.id}`}
                            to={`/workers/${timesheet.worker.id}`}
                          >
                            <Body color="theme" weight="medium">
                              {timesheet.worker.name}
                            </Body>
                          </Link>
                          <Body>{timesheet.worker.email}</Body>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Link
                            id={`job-link-${timesheet.job.id}`}
                            to={`/jobs/${timesheet.job.id}`}
                          >
                            <Body color="theme" weight="medium">
                              {timesheet.job.title || 'Untitled Job'}
                            </Body>
                          </Link>
                          <Body>{timesheet.job.skill_name}</Body>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Link
                            id={`account-link-${timesheet.account.id}`}
                            to={`/clients/${timesheet.account.id}`}
                          >
                            <Body color="theme" weight="medium">
                              {timesheet.account.name}
                            </Body>
                          </Link>
                          <Body>{timesheet.account.cut_off}</Body>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          {getStatusChip(timesheet)}
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          {getIssuesSeverityChip(timesheet.issues)}
                          {timesheet.issues.has_problems && (
                            <Body>{timesheet.issues.problems_count} total</Body>
                          )}
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Body>
                            {timesheet.job.first_shift_start_at
                              ? formatDate(timesheet.job.first_shift_start_at)
                              : 'N/A'}
                          </Body>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <IconicButton
                            a11yLabel="View Timesheet Logs"
                            appearance="primary"
                            icon={faEye}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowTimesheet(timesheet);
                            }}
                          />
                        </TextStack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            ) : !loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADER_FIELDS.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <Body>No timesheets found.</Body>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {loading && (
          <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
            <LoadingState
              overlayColor="white"
              text="Loading timesheet health checks"
            />
          </Stack>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            <Body>{error}</Body>
          </div>
        )}
      </Card>

      {/* Timesheet Logs Modal */}
      <TimesheetDetailsModal
        timesheetId={selectedTimesheetId}
        isOpen={showTimesheetModal}
        onClose={handleHideModal}
      />
    </Page>
  );
};

export default TimesheetHealthChecks;
